import { useState } from "react"
import { Check, Trash2 } from "lucide-react"
import { toast } from "@/components/portal/Toast"
import { parseServerDate } from "@/lib/utils"
import { PersonPunkt } from "./Person"
import {
  anzeigeName, tagInTagen, tagKurz, useNotizAnlegen, useNotizen, useNotizLoeschen, useTodoAbhaken,
  useAnsichtZahlen, useZustaendige, type MailNotiz,
} from "./zusammenarbeit"

function wann(iso: string): string {
  const d = parseServerDate(iso)
  if (!d || isNaN(d.getTime())) return ""
  const heute = new Date()
  const gestern = new Date(); gestern.setDate(heute.getDate() - 1)
  const zeit = d.toLocaleTimeString("de-DE", { hour: "2-digit", minute: "2-digit" })
  if (d.toDateString() === heute.toDateString()) return `heute ${zeit}`
  if (d.toDateString() === gestern.toDateString()) return `gestern ${zeit}`
  return `${d.toLocaleDateString("de-DE", { day: "2-digit", month: "2-digit" })} ${zeit}`
}

/**
 * Interne Notizen am Verlauf (14.10.2026, Marlene: „Kommentare hinterlegen,
 * damit man Informationen ablegen und übergeben kann").
 *
 * Nie an den Kunden. Mit „To-do" bleibt die Mail offen, bis es abgehakt ist.
 */
export function NotizenPanel({ emailId, tenant }: { emailId: number; tenant?: string | null }) {
  const q = useNotizen(emailId)
  const ich = useAnsichtZahlen(tenant || "").data?.ich || null
  const personen = useZustaendige()
  const anlegen = useNotizAnlegen(emailId)
  const abhaken = useTodoAbhaken(emailId)
  const loeschen = useNotizLoeschen(emailId)
  const [text, setText] = useState("")
  const [todo, setTodo] = useState(false)
  const [fuer, setFuer] = useState<string>("")
  const [bis, setBis] = useState<string>("")

  const speichern = () => {
    if (!text.trim()) return
    anlegen.mutate({ text: text.trim(), todo, todo_fuer: todo ? (fuer || ich) : null, faellig_am: todo && bis ? bis : null }, {
      onSuccess: () => {
        setText(""); setTodo(false); setFuer(""); setBis("")
        toast(todo ? "To-do gespeichert – die Mail bleibt offen" : "Notiz gespeichert", "success")
      },
      onError: (e) => toast("Speichern fehlgeschlagen: " + (e instanceof Error ? e.message : String(e)), "error"),
    })
  }

  const notizen = q.data?.notizen || []
  return (
    <div className="ev2-notizen" data-testid="notizen">
      {q.isLoading && <div className="ev2-leise">Notizen werden geladen …</div>}
      {q.isError && <div className="ev2-leise">Notizen konnten nicht geladen werden.</div>}
      {q.data && notizen.length === 0 && (
        <div className="ev2-leise">
          Noch keine Notiz. Schreib hier rein, was die Kollegin wissen muss – der Kunde sieht das nie.
        </div>
      )}
      {notizen.map((n: MailNotiz) => {
        const offenesTodo = n.todo && !n.erledigt_am
        return (
          <div key={n.id} className={offenesTodo ? "ev2-notiz todo" : "ev2-notiz"}>
            <div className="ev2-notiz-kopf">
              <PersonPunkt name={n.autor} groesse={20} titel={anzeigeName(n.autor) || "unbekannt"} />
              <b>{n.autor === ich ? "Du" : anzeigeName(n.autor) || "Unbekannt"}</b>
              <span>{wann(n.created_at)}</span>
              {n.todo && (
                <span className={n.erledigt_am ? "ev2-notiz-marke fertig" : "ev2-notiz-marke"}>
                  {n.erledigt_am ? "To-do erledigt" : `To-do${n.todo_fuer ? " · " + anzeigeName(n.todo_fuer) : ""}${n.faellig_am ? " · bis " + tagKurz(n.faellig_am) : ""}`}
                </span>
              )}
              <span style={{ flex: 1 }} />
              {n.autor === ich && (
                <button type="button" className="ev2-icon-knopf" aria-label="Notiz löschen" title="Notiz löschen"
                  onClick={() => { if (confirm("Diese Notiz löschen?")) loeschen.mutate(n.id) }}>
                  <Trash2 className="w-3.5 h-3.5" />
                </button>
              )}
            </div>
            <div className="ev2-notiz-text">{n.text}</div>
            {n.todo && (
              <label className="ev2-notiz-haken">
                <input type="checkbox" checked={!!n.erledigt_am} disabled={abhaken.isPending}
                  onChange={(e) => abhaken.mutate({ id: n.id, erledigt: e.target.checked })} />
                erledigt
              </label>
            )}
          </div>
        )
      })}

      <div className="ev2-notiz-eingabe">
        <label htmlFor={`ev2-notiz-${emailId}`} className="ev2-leise" style={{ fontSize: 12 }}>Interne Notiz</label>
        <textarea id={`ev2-notiz-${emailId}`} rows={3} value={text} onChange={(e) => setText(e.target.value)}
          onKeyDown={(e) => { if ((e.metaKey || e.ctrlKey) && e.key === "Enter") speichern() }}
          placeholder="Info für Kollegen …" />
        <div className="ev2-notiz-leiste">
          <button type="button" aria-pressed={todo} className={todo ? "ev2-pille aktiv" : "ev2-pille"}
            onClick={() => setTodo(!todo)} title="Mail bleibt offen, bis das To-do abgehakt ist">
            <Check className="w-3 h-3" aria-hidden /> To-do
          </button>
          {todo && (
            <>
              <select aria-label="Für wen" value={fuer} onChange={(e) => setFuer(e.target.value)}>
                <option value="">für mich</option>
                {(personen.data || []).filter((p) => p !== ich).map((p) => (
                  <option key={p} value={p}>für {anzeigeName(p)}</option>
                ))}
              </select>
              <select aria-label="Bis wann" value={bis} onChange={(e) => setBis(e.target.value)}>
                <option value="">ohne Datum</option>
                <option value={tagInTagen(1)}>bis morgen</option>
                <option value={tagInTagen(7)}>in einer Woche</option>
              </select>
            </>
          )}
          <span style={{ flex: 1 }} />
          <button type="button" className="pp-btn pp-btn-primary pp-btn-sm" disabled={!text.trim() || anlegen.isPending}
            onClick={speichern}>
            {anlegen.isPending ? "Speichert…" : "Speichern"}
          </button>
        </div>
      </div>
    </div>
  )
}
